import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { AuthUser } from '../types';
import { authService } from '../services/auth.service';
import { tokenStore, refreshSession } from '../services/api-client';

export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

interface AuthCtx {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setSession: (tokens: SessionTokens) => void;
  /** Patch verification flags on the cached user after a verify step. */
  patchUser: (changes: Partial<AuthUser>) => void;
  hasPermission: (p: string) => boolean;
  hasRole: (r: string) => boolean;
}

const AuthContext = createContext<AuthCtx | null>(null);

const USER_STORAGE_KEY = 'authUser';

/**
 * Rehydrates the user object synchronously from localStorage before the first
 * render. Without this, every page reload starts with user=null and the
 * AuthGuard redirects to /login — even when the refresh token is still valid.
 */
function readStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(readStoredUser);
  // Only "loading" when we have a refresh token to validate. If we have no
  // session at all, skip the spinner entirely.
  const [isLoading, setIsLoading] = useState(() => !!tokenStore.getRefresh());

  useEffect(() => {
    const refresh = tokenStore.getRefresh();
    if (refresh) {
      // refreshSession() is single-flight and shared with the API
      // interceptor — see api-client.ts. Without sharing, this boot-time
      // refresh and the first authed request would both POST /auth/refresh
      // with the same (rotating) token, and the loser's 401 would log the
      // user out right after login.
      refreshSession()
        .then(({ user: freshUser }) => {
          // Backend now returns the up-to-date AuthUser on every refresh —
          // we overwrite the localStorage snapshot so role / permission
          // changes (e.g. just became PROVIDER) are reflected immediately
          // on next render without a manual logout.
          if (freshUser) {
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(freshUser));
            setUser(freshUser as AuthUser);
          }
          setIsLoading(false);
        })
        .catch(() => {
          tokenStore.clearAll();
          localStorage.removeItem(USER_STORAGE_KEY);
          setUser(null);
          setIsLoading(false);
        });
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
      setUser(null);
    }
    const onLogout = () => {
      setUser(null);
      tokenStore.clearAll();
      localStorage.removeItem(USER_STORAGE_KEY);
    };
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  const setSession = useCallback((tokens: SessionTokens) => {
    tokenStore.setAccess(tokens.accessToken);
    tokenStore.setRefresh(tokens.refreshToken);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(tokens.user));
    setUser(tokens.user);
  }, []);

  const patchUser = useCallback((changes: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...changes };
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await authService.login(email, password);
      setSession(result);
    },
    [setSession],
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      tokenStore.clearAll();
      localStorage.removeItem(USER_STORAGE_KEY);
      setUser(null);
    }
  }, []);

  // Defensive: an AuthUser cached in localStorage from an older deploy might
  // be missing `permissions` or `roles` (schema drift between releases).
  // Without these guards, a stale cache crashes the app at boot with
  // "Cannot read properties of undefined (reading 'includes' / 'map')".
  const hasPermission = useCallback(
    (p: string) => (user?.permissions ?? []).includes(p),
    [user],
  );
  const hasRole = useCallback(
    (r: string) => (user?.roles ?? []).includes(r as never),
    [user],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        login,
        logout,
        setSession,
        patchUser,
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
